import React, { useState, useEffect } from 'react';

import axios from '../api/axios';

import { getCurrentUser } from '../auth/LoginButton';

import { getUserRoles } from '../rbac/roles';



interface AnswerValue {

  questionId?: string;

  question_id?: string;

  value: any;

}



interface SurveyResponse {

  id: string;

  user_id?: string | null;

  submitted_at?: string;

  timestamp?: string;

  campaign_id?: string;

  respondent_type?: string;

  answers: AnswerValue[];

}



interface Question {

  id: string;

  title?: string;

  label?: string;

  type: string;

  section?: string;

}



interface Survey {

  id: string;

  title: string;

  questions: Question[];

}



interface ViewResponsesProps {

  surveyId: string;

  onClose: () => void;

}



const ViewResponses: React.FC<ViewResponsesProps> = ({ surveyId, onClose }) => {

  const [responses, setResponses] = useState<SurveyResponse[]>([]);

  const [survey, setSurvey] = useState<Survey | null>(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState<string | null>(null);

  const [selectedIndex, setSelectedIndex] = useState(0);

  const [search, setSearch] = useState('');

  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'name'>('newest');

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const [deleting, setDeleting] = useState(false);

  // Only write-capable roles may delete responses. Read-only roles (Survey.Viewer)
  // — now legitimately allowed to VIEW this page — see no delete control. The backend
  // delete_survey_response endpoint enforces the same rule (defense-in-depth).
  const _me = getCurrentUser();
  const _myRoles = getUserRoles(_me);
  const _isWriteAdmin = _myRoles.some(r => ['Survey.Admin', 'Survey.Agent'].includes(r));
  const _isCreator = _myRoles.includes('Survey.Creator');
  // Write-admins (Survey.Admin / Survey.Agent) may delete any response; a Survey.Creator
  // may delete only on a survey they own — mirrors the backend authorization so we never
  // render a delete control that is guaranteed to 403. Read-only roles see no control.
  const canDelete = _isWriteAdmin || (_isCreator && !!survey && (survey as any).creator_id === _me?.email);



  useEffect(() => { fetchData(); }, [surveyId]);



  const fetchData = async () => {

    try {

      setLoading(true);

      const [surveyRes, responsesRes] = await Promise.all([

        axios.get(`/surveys/${surveyId}`),

        axios.get(`/surveys/${surveyId}/responses`),

      ]);

      setSurvey(surveyRes.data);

      const arr = responsesRes.data.responses || responsesRes.data || [];

      setResponses(Array.isArray(arr) ? arr : []);

      setError(null);

    } catch (err: any) {

      setError(err.response?.data?.message || 'Failed to load responses');

    } finally {

      setLoading(false);

    }

  };



  /** Parse ISO date string Ã¢â‚¬â€ handles Python format without trailing Z */

  const parseDate = (raw?: string): Date | null => {

    if (!raw) return null;

    const normalized = raw.endsWith('Z') ? raw : raw + 'Z';

    const d = new Date(normalized);

    return isNaN(d.getTime()) ? null : d;

  };



  const formatDate = (raw?: string): string => {

    const d = parseDate(raw);

    if (!d) return raw || '--';

    return d.toLocaleString('en-US', {

      month: 'short', day: 'numeric', year: 'numeric',

      hour: 'numeric', minute: '2-digit', hour12: true,

    });

  };



  const formatDateShort = (raw?: string): string => {

    const d = parseDate(raw);

    if (!d) return raw || '--';

    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  };



  /** Get answer value Ã¢â‚¬â€ handle both camelCase and snake_case questionId */

  const getAnswer = (response: SurveyResponse, questionId: string): any => {

    if (!response.answers) return null;

    const a = response.answers.find(

      ans => (ans.questionId || ans.question_id) === questionId

    );

    return a?.value ?? null;

  };



  /** Get the display title of a question */

  const getTitle = (q: Question): string => q.title || q.label || q.id;



  /** Get the respondent's name from answers (q1 or q2 tend to be name) */

  const getRespondentName = (response: SurveyResponse): string => {

    if (!survey) return 'Anonymous';

    // Try common name question IDs

    for (const qid of ['q1', 'q2', 'name']) {

      const v = getAnswer(response, qid);

      if (v && typeof v === 'string' && v.trim()) return v.trim();

    }

    // Try first text answer

    if (survey.questions.length > 0) {

      const firstQ = survey.questions.find(q => q.type !== 'textblock');

      if (firstQ) {

        const v = getAnswer(response, firstQ.id);

        if (v) return String(v).slice(0, 40);

      }

    }

    return `Response #${responses.indexOf(response) + 1}`;

  };



  const formatValue = (value: any): string => {

    if (value === null || value === undefined) return '--';

    if (Array.isArray(value)) return value.join(', ');

    if (typeof value === 'object') return JSON.stringify(value);

    return String(value);

  };



  /** Group non-textblock questions by section */

  const getSections = (questions: Question[]): { section: string; questions: Question[] }[] => {

    const map: Map<string, Question[]> = new Map();

    for (const q of questions) {

      if (q.type === 'textblock') continue;

      const sec = q.section || 'General';

      if (!map.has(sec)) map.set(sec, []);

      map.get(sec)!.push(q);

    }

    return Array.from(map.entries()).map(([section, questions]) => ({ section, questions }));

  };



  const deleteResponse = async (responseId: string) => {
    setDeleting(true);
    try {
      await axios.delete(`/surveys/${surveyId}/responses/${responseId}`);
      const updated = responses.filter(r => r.id !== responseId);
      setResponses(updated);
      setSelectedIndex(i => Math.min(i, Math.max(0, updated.length - 1)));
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete response');
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const exportToCSV = () => {

    if (!survey || responses.length === 0) return;

    const answerQuestions = survey.questions.filter(q => q.type !== 'textblock');

    const headers = ['Response ID', 'Submitted At', 'Type', ...answerQuestions.map(q => getTitle(q))];

    const rows = responses.map(r => {

      const dateStr = formatDate(r.submitted_at || r.timestamp);

      return [

        r.id,

        dateStr,

        r.respondent_type || 'anonymous',

        ...answerQuestions.map(q => {

          const v = getAnswer(r, q.id);

          return `"${formatValue(v).replace(/"/g, '""')}"`;

        }),

      ].join(',');

    });

    const csv = [headers.join(','), ...rows].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });

    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');

    a.href = url;

    a.download = `${(survey.title || 'responses').replace(/[^a-z0-9]/gi, '_')}_responses.csv`;

    document.body.appendChild(a);

    a.click();

    document.body.removeChild(a);

    window.URL.revokeObjectURL(url);

  };



  // Ã¢â€â‚¬Ã¢â€â‚¬ Loading / Error states Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬

  if (loading) {

    return (

      <div style={s.overlay}>

        <div style={s.modal}>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 16 }}>

            <div style={s.spinner} />

            <p style={{ color: 'var(--accent)', margin: 0 }}>Loading responses...</p>

          </div>

        </div>

      </div>

    );

  }



  if (error) {

    return (

      <div style={s.overlay}>

        <div style={{ ...s.modal, alignItems: 'center', justifyContent: 'center', gap: 16 }}>

          <p style={{ color: 'var(--error)', fontSize: 16 }}>{error}</p>

          <button onClick={onClose} style={s.btn}>Close</button>

        </div>

      </div>

    );

  }



  // Ã¢â€â‚¬Ã¢â€â‚¬ Filter responses by search Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬

  const sortedResponses = [...responses].sort((a, b) => {
    if (sortBy === 'newest') {
      const da = parseDate(a.submitted_at || a.timestamp)?.getTime() ?? 0;
      const db = parseDate(b.submitted_at || b.timestamp)?.getTime() ?? 0;
      return db - da;
    }
    if (sortBy === 'oldest') {
      const da = parseDate(a.submitted_at || a.timestamp)?.getTime() ?? 0;
      const db = parseDate(b.submitted_at || b.timestamp)?.getTime() ?? 0;
      return da - db;
    }
    // name A-Z
    return getRespondentName(a).localeCompare(getRespondentName(b));
  });

  const filteredResponses = sortedResponses.filter(r => {

    if (!search.trim()) return true;

    const name = getRespondentName(r).toLowerCase();

    const date = formatDate(r.submitted_at || r.timestamp).toLowerCase();

    const q = search.toLowerCase();

    return name.includes(q) || date.includes(q);

  });



  const selected = filteredResponses[selectedIndex] || filteredResponses[0];

  const sections = survey ? getSections(survey.questions) : [];



  // Ã¢â€â‚¬Ã¢â€â‚¬ Main render Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬

  return (

    <div style={s.overlay}>

      <div style={s.modal}>



        {/* Ã¢â€â‚¬Ã¢â€â‚¬ Header Ã¢â€â‚¬Ã¢â€â‚¬ */}

        <div style={s.header}>

          <div>

            <h2 style={s.title}>Survey Responses</h2>

            <p style={s.subtitle}>{survey?.title}</p>

          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>

            {responses.length > 0 && (

              <button onClick={exportToCSV} style={s.exportBtn}>

                Export CSV

              </button>

            )}

            <button onClick={onClose} style={s.closeBtn}>Close</button>

          </div>

        </div>



        {/* Ã¢â€â‚¬Ã¢â€â‚¬ Stats bar Ã¢â€â‚¬Ã¢â€â‚¬ */}

        <div style={s.statsBar}>

          <span style={s.statChip}><strong>{responses.length}</strong>&nbsp;{responses.length === 1 ? 'Response' : 'Responses'}</span>

          <span style={s.statChip}><strong>{survey?.questions.filter(q => q.type !== 'textblock').length ?? 0}</strong>&nbsp;Questions</span>

          {responses.length > 0 && (
            <>
              <select
                style={{ ...s.search, width: 160, cursor: 'pointer' }}
                value={sortBy}
                onChange={e => { setSortBy(e.target.value as typeof sortBy); setSelectedIndex(0); }}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="name">Name A-Z</option>
              </select>
              <input
                style={s.search}
                placeholder="Search respondents..."
                value={search}
                onChange={e => { setSearch(e.target.value); setSelectedIndex(0); }}
              />
            </>
          )}

        </div>



        {/* Ã¢â€â‚¬Ã¢â€â‚¬ Body Ã¢â€â‚¬Ã¢â€â‚¬ */}

        {filteredResponses.length === 0 ? (

          <div style={s.empty}>

            <div style={{ fontSize: 64 }}>No Responses</div>

            <h3 style={{ color: 'var(--accent)', margin: '12px 0 4px' }}>

              {responses.length === 0 ? 'No responses yet' : 'No matches found'}

            </h3>

            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>

              {responses.length === 0 ? 'Share your survey to start collecting responses' : 'Try a different search term'}

            </p>

          </div>

        ) : (

          <div style={s.body}>



            {/* Ã¢â€â‚¬Ã¢â€â‚¬ Left: response list Ã¢â€â‚¬Ã¢â€â‚¬ */}

            <div style={s.list}>

              {filteredResponses.map((response, idx) => {

                const isActive = idx === selectedIndex;

                const name = getRespondentName(response);

                const date = formatDateShort(response.submitted_at || response.timestamp);

                const initials = name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('');

                return (

                  <div

                    key={response.id}

                    onClick={() => { setSelectedIndex(idx); setDeleteConfirm(null); }}

                    style={{

                      ...s.listItem,

                      background: isActive ? 'var(--accent)' : 'transparent',

                      color: isActive ? 'var(--button-text)' : 'var(--text-primary)',

                      flexDirection: 'column',

                      alignItems: 'stretch',

                      gap: 0,

                    }}

                  >

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>

                      <div style={{

                        ...s.avatar,

                        background: isActive ? 'rgba(255,255,255,0.25)' : 'var(--bg-secondary)',

                        color: isActive ? 'var(--button-text)' : 'var(--accent)',

                      }}>

                        {initials || '#'}

                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>

                        <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>

                          {name}

                        </div>

                        <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2 }}>{date}</div>

                      </div>

                      {canDelete && (

                      <button

                        onClick={e => { e.stopPropagation(); setDeleteConfirm(response.id); }}

                        title="Delete response"

                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14, color: isActive ? 'rgba(255,255,255,0.7)' : 'var(--error, #e53e3e)', flexShrink: 0, padding: '2px 4px', borderRadius: 4 }}

                      >X</button>

                      )}

                    </div>

                    {deleteConfirm === response.id && (

                      <div onClick={e => e.stopPropagation()} style={{ marginTop: 8, padding: '6px 8px', background: 'rgba(229,62,62,0.12)', border: '1px solid var(--error,#e53e3e)', borderRadius: 6, fontSize: 12 }}>

                        <div style={{ marginBottom: 6, fontWeight: 600 }}>Delete this response?</div>

                        <div style={{ display: 'flex', gap: 6 }}>

                          <button disabled={deleting} onClick={e => { e.stopPropagation(); deleteResponse(response.id); }} style={{ flex: 1, background: 'var(--error,#e53e3e)', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 0', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>{deleting ? '...' : 'Delete'}</button>

                          <button onClick={e => { e.stopPropagation(); setDeleteConfirm(null); }} style={{ flex: 1, background: 'var(--card-bg)', color: 'var(--text-primary)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '4px 0', cursor: 'pointer', fontSize: 12 }}>Cancel</button>

                        </div>

                      </div>

                    )}

                  </div>

                );

              })}

            </div>



            {/* Ã¢â€â‚¬Ã¢â€â‚¬ Right: response detail Ã¢â€â‚¬Ã¢â€â‚¬ */}

            {selected && (

              <div style={s.detail}>

                {/* Detail header */}

                <div style={s.detailHeader}>

                  <div>

                    <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--accent)' }}>

                      {getRespondentName(selected)}

                    </div>

                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>

                      Submitted: {formatDate(selected.submitted_at || selected.timestamp)}

                      {selected.respondent_type && (

                        <span style={{ marginLeft: 12, padding: '2px 8px', borderRadius: 4, background: 'var(--bg-secondary)', border: '1px solid var(--card-border)', fontSize: 11 }}>

                          {selected.respondent_type}

                        </span>

                      )}

                    </div>

                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>

                    <button

                      disabled={selectedIndex === 0}

                      onClick={() => setSelectedIndex(i => Math.max(0, i - 1))}

                      style={{ ...s.navBtn, opacity: selectedIndex === 0 ? 0.3 : 1 }}

                    >Prev</button>

                    <button

                      disabled={selectedIndex >= filteredResponses.length - 1}

                      onClick={() => setSelectedIndex(i => Math.min(filteredResponses.length - 1, i + 1))}

                      style={{ ...s.navBtn, opacity: selectedIndex >= filteredResponses.length - 1 ? 0.3 : 1 }}

                    >Next</button>

                  </div>

                </div>



                {/* Answers grouped by section */}

                <div style={s.detailBody}>

                  {sections.map(({ section, questions }) => (

                    <div key={section} style={s.section}>

                      <div style={s.sectionHeader}>{section}</div>

                      <div style={s.grid}>

                        {questions.map(q => {

                          const value = getAnswer(selected, q.id);

                          const hasAnswer = value !== null && value !== undefined && value !== '';

                          return (

                            <div key={q.id} style={s.cell}>

                              <div style={s.cellLabel}>{getTitle(q)}</div>

                              <div style={{ ...s.cellValue, color: hasAnswer ? 'var(--text-primary)' : 'var(--text-secondary)', fontStyle: hasAnswer ? 'normal' : 'italic' }}>

                                {hasAnswer ? formatValue(value) : '--'}

                              </div>

                            </div>

                          );

                        })}

                      </div>

                    </div>

                  ))}

                </div>

              </div>

            )}

          </div>

        )}

      </div>

    </div>

  );

};



// Ã¢â€â‚¬Ã¢â€â‚¬ Styles Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬

const s: Record<string, React.CSSProperties> = {

  overlay: {

    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,

    background: 'rgba(0,0,0,0.72)',

    display: 'flex', justifyContent: 'center', alignItems: 'center',

    zIndex: 1000, padding: 16,

  },

  modal: {

    background: 'var(--card-bg)',

    borderRadius: 16,

    width: '100%',

    maxWidth: 1300,

    height: '92vh',

    overflow: 'hidden',

    display: 'flex',

    flexDirection: 'column',

    border: '2px solid var(--card-border)',

    boxShadow: '0 24px 64px rgba(0,0,0,0.55)',

  },

  header: {

    padding: '18px 24px',

    borderBottom: '2px solid var(--card-border)',

    display: 'flex',

    justifyContent: 'space-between',

    alignItems: 'center',

    background: 'var(--bg-secondary)',

    flexShrink: 0,

  },

  title: { margin: '0 0 4px 0', color: 'var(--accent)', fontSize: 22, fontWeight: 700 },

  subtitle: { margin: 0, color: 'var(--text-secondary)', fontSize: 13 },

  closeBtn: {

    background: 'transparent', border: '1px solid var(--card-border)',

    color: 'var(--text-primary)', fontSize: 18, cursor: 'pointer',

    width: 34, height: 34, borderRadius: 8,

    display: 'flex', alignItems: 'center', justifyContent: 'center',

  },

  exportBtn: {

    background: 'var(--button-bg)', color: 'var(--button-text)',

    border: 'none', padding: '7px 14px',

    borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,

  },

  statsBar: {

    padding: '10px 24px',

    borderBottom: '1px solid var(--input-border)',

    display: 'flex', alignItems: 'center', gap: 12,

    background: 'var(--bg-secondary)', flexShrink: 0,

  },

  statChip: {

    background: 'var(--card-bg)',

    border: '1px solid var(--card-border)',

    borderRadius: 20, padding: '3px 12px',

    fontSize: 13, color: 'var(--text-primary)',

  },

  search: {

    marginLeft: 'auto',

    padding: '6px 12px',

    borderRadius: 8,

    border: '1px solid var(--input-border)',

    background: 'var(--card-bg)',

    color: 'var(--text-primary)',

    fontSize: 13,

    width: 200,

    outline: 'none',

  },

  body: {

    flex: 1, display: 'flex', overflow: 'hidden',

  },

  list: {

    width: 240, minWidth: 200, flexShrink: 0,

    borderRight: '1px solid var(--card-border)',

    overflowY: 'auto',

    background: 'var(--bg-secondary)',

  },

  listItem: {

    display: 'flex', alignItems: 'center', gap: 10,

    padding: '10px 14px', cursor: 'pointer',

    borderBottom: '1px solid var(--card-border)',

    transition: 'background 0.15s',

  },

  avatar: {

    width: 34, height: 34, borderRadius: 17,

    display: 'flex', alignItems: 'center', justifyContent: 'center',

    fontSize: 12, fontWeight: 700, flexShrink: 0,

  },

  detail: {

    flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',

  },

  detailHeader: {

    padding: '16px 24px',

    borderBottom: '1px solid var(--card-border)',

    display: 'flex', justifyContent: 'space-between', alignItems: 'center',

    background: 'var(--bg-secondary)', flexShrink: 0,

  },

  navBtn: {

    background: 'var(--card-bg)', border: '1px solid var(--card-border)',

    color: 'var(--text-primary)', fontSize: 16, cursor: 'pointer',

    width: 32, height: 32, borderRadius: 8,

    display: 'flex', alignItems: 'center', justifyContent: 'center',

  },

  detailBody: {

    flex: 1, overflowY: 'auto', padding: '20px 24px',

    display: 'flex', flexDirection: 'column', gap: 24,

  },

  section: { display: 'flex', flexDirection: 'column', gap: 12 },

  sectionHeader: {

    fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',

    textTransform: 'uppercase', color: 'var(--accent)',

    paddingBottom: 6, borderBottom: '2px solid var(--accent)',

  },

  grid: {

    display: 'grid',

    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',

    gap: 10,

  },

  cell: {

    background: 'var(--bg-secondary)',

    border: '1px solid var(--card-border)',

    borderRadius: 8, padding: '10px 14px',

  },

  cellLabel: {

    fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',

    marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em',

  },

  cellValue: { fontSize: 14, lineHeight: 1.5, wordBreak: 'break-word' },

  empty: {

    flex: 1, display: 'flex', flexDirection: 'column',

    alignItems: 'center', justifyContent: 'center', padding: 60,

  },

  spinner: {

    width: 44, height: 44,

    border: '4px solid var(--input-border)',

    borderTop: '4px solid var(--accent)',

    borderRadius: '50%',

    animation: 'spin 1s linear infinite',

  },

  btn: {

    background: 'var(--button-bg)', color: 'var(--button-text)',

    border: 'none', padding: '10px 22px',

    borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600,

  },

};



export default ViewResponses;



